import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config/api";
import {
  bindStorefrontRefresh,
  STOREFRONT_REFRESH_SCOPES,
} from "../utils/storefrontRefresh";
import "./ContactPage.css";

const DEFAULT_CONTACT_PAGE_CONTENT = {
  showContactInfoSection: true,
  contactInfoTitle: "Get in Touch",
  contactInfoDescription:
    "We'd love to hear from you! Whether you have a question about our jewelry, want to discuss a custom piece, or need support, our team is here to help.",
  contactEmailLabel: "Email",
  contactEmail: "support.panchalijewels@gmail.com",
  contactPhoneLabel: "Phone",
  contactPhone: "+91 96965 81515",
  contactAddressLabel: "Address",
  contactAddress: "123, Panchali Jewels Lane, Mumbai, India",
  showBusinessSection: true,
  businessTitle: "Business Inquiries",
  businessDescription:
    "For wholesale, partnership, or press inquiries, please email us at",
  businessEmail: "business@panchalijewels.com",
  showFormSection: true,
  formTitle: "Send Us a Message",
  formDescription: "",
  formNameLabel: "Name",
  formEmailLabel: "Email",
  formMessageLabel: "Message",
  submitButtonLabel: "Send Message",
  submittingButtonLabel: "Sending...",
  successMessage: "Thank you for reaching out! We'll get back to you soon.",
  errorMessage: "Failed to send message. Please try again.",
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [content, setContent] = useState(DEFAULT_CONTACT_PAGE_CONTENT);

  useEffect(() => {
    const fetchContactPageContent = async () => {
      try {
        const response = await axios.get(`${API_BASE}/contact-page`);
        setContent({
          ...DEFAULT_CONTACT_PAGE_CONTENT,
          ...(response.data || {}),
        });
      } catch (fetchError) {
        console.error("Failed to load contact page content:", fetchError);
      }
    };

    fetchContactPageContent();

    return bindStorefrontRefresh(fetchContactPageContent, {
      scopes: [STOREFRONT_REFRESH_SCOPES.CONTACT],
    });
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/contact/submit`, form);
      
      if (response.data.success) {
        setSubmitted(true);
        setForm({ name: "", email: "", message: "" });
      }
    } catch (err) {
      setError(err.response?.data?.message || content.errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const hasLeftSection = content.showContactInfoSection || content.showBusinessSection;
  const hasRightSection = content.showFormSection;
  const phoneHref = content.contactPhone.replace(/[^\d+]/g, "");
  const showBusinessCopy = content.businessDescription || content.businessEmail;
  const contactDetailRows = [
    {
      label: content.contactEmailLabel,
      value: content.contactEmail,
      href: content.contactEmail ? `mailto:${content.contactEmail}` : "",
    },
    {
      label: content.contactPhoneLabel,
      value: content.contactPhone,
      href: content.contactPhone ? `tel:${phoneHref}` : "",
    },
    {
      label: content.contactAddressLabel,
      value: content.contactAddress,
      href: "",
    },
  ].filter((row) => row.value);

  return (
    <div className="contact-container">
      <div className="contact-main-content">
        {hasLeftSection ? (
          <div className="contact-left-section">
            {content.showContactInfoSection ? (
              <section className="contact-info-section">
                {content.contactInfoTitle ? <h2>{content.contactInfoTitle}</h2> : null}
                {content.contactInfoDescription ? <p>{content.contactInfoDescription}</p> : null}
                {contactDetailRows.length ? (
                  <div className="contact-details">
                    {contactDetailRows.map((row) => (
                      <div key={`${row.label}-${row.value}`}>
                        {row.label ? <b>{row.label}:</b> : null}
                        {row.label ? " " : null}
                        {row.href ? <a href={row.href}>{row.value}</a> : row.value}
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            {content.showBusinessSection ? (
              <section className="contact-business-section">
                {content.businessTitle ? <h2>{content.businessTitle}</h2> : null}
                {showBusinessCopy ? (
                  <p>
                    {content.businessDescription}
                    {content.businessDescription ? " " : ""}
                    {content.businessEmail ? (
                      <a href={`mailto:${content.businessEmail}`}>{content.businessEmail}</a>
                    ) : null}
                    {content.businessDescription && content.businessEmail ? "." : ""}
                  </p>
                ) : null}
              </section>
            ) : null}
          </div>
        ) : null}

        {hasRightSection ? (
          <div className="contact-right-section">
            <section className="contact-form-section">
              {content.formTitle ? <h2>{content.formTitle}</h2> : null}
              {content.formDescription ? <p>{content.formDescription}</p> : null}
              {submitted ? (
                content.successMessage ? (
                  <div className="contact-success">{content.successMessage}</div>
                ) : null
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  {error && <div className="contact-error">{error}</div>}
                  <div className="form-group form-group-compact">
                    {content.formNameLabel ? (
                      <label htmlFor="name">{content.formNameLabel}</label>
                    ) : null}
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      aria-label={content.formNameLabel || "Name"}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group form-group-compact">
                    {content.formEmailLabel ? (
                      <label htmlFor="email">{content.formEmailLabel}</label>
                    ) : null}
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      aria-label={content.formEmailLabel || "Email"}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    {content.formMessageLabel ? (
                      <label htmlFor="message">{content.formMessageLabel}</label>
                    ) : null}
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      aria-label={content.formMessageLabel || "Message"}
                      required
                      rows={5}
                    />
                  </div>
                  <button type="submit" className="contact-submit-btn" disabled={loading}>
                    {loading ? content.submittingButtonLabel : content.submitButtonLabel}
                  </button>
                </form>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ContactPage;
